import { JSX, useContext } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { RouteEnum } from "../common/enums/RouteEnum";
import { useAppSelector } from "./hooks";
import { Wrapper } from "../wrappers/app/Wrapper";
import ProductRangePageContainer from "../pages/productRangePage/ProductRangePageContainer";
import ProductDetailsPageContainer from "../pages/productDetail/ProductDetailsPageContainer";
import CollectionsPageContainer from "../pages/collectionsPage/CollectionsPageContainer";
import { DeleteCollectionModalContainer } from "../modals/deleteCollectionModal/DeleteCollectionModalContainer";
import { DeleteCollectionProductModalContainer } from "../modals/deleteCollectionProductModal/DeleteCollectionProductModalContainer";
import { EditCollectionModalContainer } from "../modals/editCollectionModal/EditCollectionModalContainer";
import { AddCollectionProductsModalContainer } from "../modals/addCollectionProductsModal/AddCollectionProductsModalContainer";
import PriceBookPageContainer from "../pages/priceBookPage/PriceBookPageContainer";
import { AddCollectionModalContainer } from "../modals/addCollectionModal/AddCollectionModalContainer";
import { ProductGalleryModal } from "../modals/productGalleryModal/ProductGalleryModal";
import SearchPageContainer from "../pages/searchPage/SearchPageContainer";
import { useGetTranslationsQuery, useGetUserInfoQuery } from "./rootReducer";
import { AuthContext, IAuthContext } from "react-oauth2-code-pkce";
import AccountPageContainer from "../pages/accountPage/AccountPageContainer";
import { LogOutRequest } from "../common/components/general/LogOutRequest";
import { RefurbishedPageContainer } from "../pages/refurbishedPage/RefurbishedPageContainer";
import RefurbishedProductDetailsPageContainer from "../pages/refurbishedProductDetail/RefurbishedProductDetailsPageContainer";
import { CartPageContainer } from "../pages/cartPage/CartPageContainer";
import { CheckoutPageContainer } from "../pages/checkoutPage/CheckoutPageContainer";
import { CheckoutThankYouPageContainer } from "../pages/checkoutThankYouPage/CheckoutThankYouPageContainer";
import { RefurbishedShopGuard } from "../pages/refurbishedPage/components/RefurbishedShopGuard";

const App = (): JSX.Element => {
    const deleteCollectionModal = useAppSelector(
        (state) => state.deleteCollectionModal,
    );
    const deleteCollectionProductModal = useAppSelector(
        (state) => state.deleteCollectionProductModal,
    );
    const editCollectionModal = useAppSelector(
        (state) => state.editCollectionModal,
    );
    const addCollectionProductsModal = useAppSelector(
        (state) => state.addCollectionProductsModal,
    );
    const addCollectionModal = useAppSelector(
        (state) => state.addCollectionModal,
    );

    const { login } = useContext<IAuthContext>(AuthContext);

    const { data: translationsData, isError: translationsError } =
        useGetTranslationsQuery();
    const { data: userInfoData, isError: userInfoError } =
        useGetUserInfoQuery();

    if (
        (!translationsData && translationsError) ||
        (!userInfoData && userInfoError)
    ) {
        localStorage.clear();
        login();
    }
    return (
        <BrowserRouter>
            <Wrapper>
                <Routes>
                    <Route
                        path="/"
                        element={
                            <Navigate to={RouteEnum.PRODUCT_RANGE} replace />
                        }
                    />
                    <Route
                        path={RouteEnum.PRODUCT_RANGE}
                        element={<ProductRangePageContainer />}
                    />
                    <Route
                        path={RouteEnum.COLLECTIONS}
                        element={<CollectionsPageContainer />}
                    />
                    <Route
                        path={RouteEnum.PRODUCT_RANGE + "/:productId"}
                        element={<ProductDetailsPageContainer />}
                    />
                    <Route
                        path={RouteEnum.ACCOUNT}
                        element={<AccountPageContainer />}
                    />
                    <Route
                        path={RouteEnum.PRICEBOOK}
                        element={<PriceBookPageContainer />}
                    />
                    <Route
                        path={RouteEnum.SEARCH}
                        element={<SearchPageContainer />}
                    />
                    <Route
                        path={RouteEnum.REFURBISHED}
                        element={
                            <RefurbishedShopGuard>
                                <RefurbishedPageContainer />
                            </RefurbishedShopGuard>
                        }
                    />
                    <Route
                        path={RouteEnum.REFURBISHED_CART}
                        element={
                            <RefurbishedShopGuard>
                                <CartPageContainer />
                            </RefurbishedShopGuard>
                        }
                    />
                    <Route
                        path={RouteEnum.REFURBISHED_CHECKOUT}
                        element={
                            <RefurbishedShopGuard>
                                <CheckoutPageContainer />
                            </RefurbishedShopGuard>
                        }
                    />
                    <Route
                        path={RouteEnum.REFURBISHED_CHECKOUT_THANK_YOU}
                        element={
                            <RefurbishedShopGuard>
                                <CheckoutThankYouPageContainer />
                            </RefurbishedShopGuard>
                        }
                    />
                    <Route
                        path={RouteEnum.REFURBISHED + "/:refurbishedProductId"}
                        element={
                            <RefurbishedShopGuard>
                                <RefurbishedProductDetailsPageContainer />
                            </RefurbishedShopGuard>
                        }
                    />
                    <Route path="/logout" element={<LogOutRequest />} />
                </Routes>

                <ProductGalleryModal />

                {deleteCollectionModal.collectionId && (
                    <DeleteCollectionModalContainer />
                )}
                {deleteCollectionProductModal.collectionId && (
                    <DeleteCollectionProductModalContainer />
                )}
                {editCollectionModal.collectionId && (
                    <EditCollectionModalContainer />
                )}
                {addCollectionProductsModal.collectionId && (
                    <AddCollectionProductsModalContainer />
                )}
                {addCollectionModal.selectedProductIds && (
                    <AddCollectionModalContainer />
                )}
            </Wrapper>
        </BrowserRouter>
    );
};

export default App;
